import { createElement } from 'react';
import { SquareCheckBigIcon } from 'lucide-react';

import { defineModule } from '@/shared/registry/types';

import { useTaskCommands, useTasksNavCounter } from './components/extensionHooks';
import { TaskSearchResult } from './components/extensions';
import { TaskCommentSearchResult } from './components/TaskCommentSearchResult';
import { LazyGroupTasksTab, LazyLinkedTasksTab, LazyProfileTasksTab, LazyTaskPanel } from './lazy';
import { setupTaskRealtime } from './realtime';

/**
 * `tasks` module (docs/PRODUCT.md "Tasks & projects", docs/modules/tasks.md): list, Kanban, deadline, "My plan" and
 * Gantt views under `/tasks/*`, the `task:<id>` / `task:new` side panels, the group tab and profile tab `tasks`, the
 * sidebar counter, ⌘K actions and the search renderer of task documents. The «Задачи» tab of the cards of CRM records
 * (`documentTabs`) lists the tasks linked to them — the ones a stage's robot created for a deal. The «Роботы» link of a
 * project leads to the page of the `bizproc` module.
 */
export const module = defineModule({
  id: 'tasks',
  routes: [
    {
      path: 'tasks/*',
      handle: { layout: 'full' },
      lazy: async () => ({ Component: (await import('./pages/TasksPage')).default }),
    },
  ],
  // The printable task card renders outside the shell (it signs in itself through RequireAuth).
  publicRoutes: [
    {
      path: 'tasks/print/:id',
      lazy: async () => ({ Component: (await import('./components/TaskPrintView')).default }),
    },
  ],
  nav: [
    {
      id: 'tasks',
      labelKey: 'tasks:nav.title',
      icon: SquareCheckBigIcon,
      to: '/tasks',
      order: 30,
      section: 'main',
      useCounter: useTasksNavCounter,
    },
  ],
  sidePanels: [{ type: 'task', component: LazyTaskPanel, size: 'xl', titleKey: 'tasks:panel.title' }],
  groupTabs: [
    {
      id: 'tasks',
      labelKey: 'tasks:tabs.group',
      order: 20,
      element: (groupId) => createElement(LazyGroupTasksTab, { groupId }),
    },
  ],
  profileTabs: [
    {
      id: 'tasks',
      labelKey: 'tasks:tabs.profile',
      order: 20,
      element: (userId) => createElement(LazyProfileTasksTab, { userId }),
    },
  ],
  documentTabs: [
    {
      id: 'tasks',
      labelKey: 'tasks:linked.tab',
      order: 50,
      documentTypes: ['crm.lead', 'crm.deal', 'crm.contact', 'crm.company'],
      element: (document) => {
        const [source = '', kind = ''] = document.type.split('.');
        return createElement(LazyLinkedTasksTab, { module: source, type: kind, id: document.id });
      },
    },
  ],
  searchRenderers: { task: TaskSearchResult, comment: TaskCommentSearchResult },
  commands: [{ id: 'tasks', headingKey: 'tasks:commands.heading', order: 30, useItems: useTaskCommands }],
  setup: setupTaskRealtime,
});
